import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IoAdd,
  IoEllipsisHorizontal,
  IoFolderOutline,
  IoCalendarOutline,
  IoChevronBack,
  IoChevronDown,
  IoPencilOutline,
  IoSearchOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { WorkoutTypeIcon } from '../../components/WorkoutTypeIcon';
import { displayDigits } from '../../utils/displayDigits';
import { MobileSheet } from '../../components/MobileSheet';
import { NmButton } from '../../components/NmButton';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmInput } from '../../components/NmInput';
import { NmIsoDateField } from '../../components/NmIsoDateField';
import { NmLoadingOverlay } from '../../components/NmLoadingOverlay';
import { NmSelect } from '../../components/NmSelect';
import { usePageRefresh } from '../../context/PageRefreshContext';
import { WorkoutStructureBarMini } from '../../components/WorkoutStructureBarMini';
import { useAthleteContext } from '../../context/AthleteContext';
import { WorkoutBuilderPage, type WorkoutBuilderEditConfig } from '../../pages/WorkoutBuilderPage';
import {
  library as libraryApi,
  workouts,
  type LibraryExercise,
  type LibraryItem,
} from '../../services/api';
import { useAthleteMetrics } from '../../hooks/useAthleteMetrics';
import { formatWeekdayDate } from '../../i18n/formatters';
import {
  apiStructureToBuilderSteps,
  formatPlannedDuration,
  type StructureParseOptions,
} from '../../utils/workoutStructure';
import { getWorkoutTypeDisplayName, sortWorkoutTypesForPicker } from '../../utils/workoutTypeDisplay';

type LibRow = LibraryItem & { collapsed: boolean };
type LibModal = { mode: 'add' | 'edit'; id?: number } | null;
type DeleteTarget = { type: 'library' | 'exercise'; id: number; name: string } | null;
type AddModal = { libraryId: number; step: 'choose' | 'quick' } | null;

/** Workout library — mirrors Ai-Tempo `Libraray.tsx` mobile UX. */
export function MobileLibraryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { effectiveUserId, coachMenuLocked, isCoach, athletes } = useAthleteContext();
  const { ftp, ltHr, thresholdSpeedMps } = useAthleteMetrics(effectiveUserId);

  const structureParseOptions: StructureParseOptions = {
    ftp: ftp ?? undefined,
    ltHr: ltHr ?? undefined,
    thresholdSpeedMps: thresholdSpeedMps ?? undefined,
  };

  const [data, setData] = useState<LibRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [libModal, setLibModal] = useState<LibModal>(null);
  const [libName, setLibName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [addModal, setAddModal] = useState<AddModal>(null);
  const [editExercise, setEditExercise] = useState<LibraryExercise | null>(null);
  const [builderEdit, setBuilderEdit] = useState<LibraryExercise | null>(null);
  const [calModal, setCalModal] = useState<LibraryExercise | null>(null);
  const [calDate, setCalDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [calAthleteIds, setCalAthleteIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('all');

  const [qaTitle, setQaTitle] = useState('');
  const [qaDuration, setQaDuration] = useState('');
  const [qaTss, setQaTss] = useState('');
  const [qaType, setQaType] = useState<number | ''>('');
  const [sportTypes, setSportTypes] = useState<{ id: number; label: string }[]>([]);

  const fetchAll = useCallback(async () => {
    setError('');
    try {
      const res = await libraryApi.getAll();
      setData((res.data?.data ?? []).map((lib) => ({ ...lib, collapsed: true })));
    } catch (e) {
      setError((e as Error)?.message ?? t('library.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  usePageRefresh(fetchAll, [fetchAll]);

  useEffect(() => {
    workouts
      .getWorkoutTypes()
      .then((res) => {
        const types = sortWorkoutTypesForPicker(res.data?.data?.workoutTypes ?? []);
        setSportTypes(types.map(type => ({
          id: type.id,
          label: getWorkoutTypeDisplayName(type, t),
        })));
      })
      .catch(() => {
        setSportTypes([]);
      });
  }, [t]);

  const toggleCollapse = (id: number) => {
    setData((prev) =>
      prev.map((lib) => (lib.id === id ? { ...lib, collapsed: !lib.collapsed } : lib)),
    );
  };

  const handleSaveLib = async () => {
    if (!libName.trim()) return;
    setBusy(true);
    setLibModal(null);
    try {
      if (libModal?.mode === 'add') await libraryApi.create(libName.trim());
      else if (libModal?.id) await libraryApi.update(libModal.id, libName.trim());
      await fetchAll();
    } catch (e) {
      setError((e as Error)?.message ?? t('library.saveFailed', 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      if (target.type === 'library') await libraryApi.delete(target.id);
      else await libraryApi.deleteExercise(target.id);
      await fetchAll();
    } catch (e) {
      setError((e as Error)?.message ?? t('library.deleteFailed', 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleQuickAdd = async () => {
    if (!qaTitle.trim() || !addModal) return;
    setBusy(true);
    setAddModal(null);
    try {
      await libraryApi.addExercise(addModal.libraryId, {
        title: qaTitle.trim(),
        workout_type_id: qaType === '' ? undefined : qaType,
        total_time_planned: qaDuration ? Number(qaDuration) : undefined,
        tss_planned: qaTss ? Number(qaTss) : undefined,
      });
      await fetchAll();
    } catch (e) {
      setError((e as Error)?.message ?? t('library.saveFailed', 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleQuickEdit = async () => {
    if (!qaTitle.trim() || !editExercise) return;
    setBusy(true);
    const ex = editExercise;
    setEditExercise(null);
    try {
      await libraryApi.updateExercise(ex.id, {
        data: {
          exercise: {
            title: qaTitle.trim(),
            workout_type_id: qaType === '' ? undefined : qaType,
            total_time_planned: qaDuration ? Number(qaDuration) : undefined,
            tss_planned: qaTss ? Number(qaTss) : undefined,
          },
        },
      });
      await fetchAll();
    } catch (e) {
      setError((e as Error)?.message ?? t('library.saveFailed', 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const openCalendarAssignment = (exercise: LibraryExercise) => {
    setCalDate(new Date().toISOString().slice(0, 10));
    setCalAthleteIds(
      isCoach && effectiveUserId != null && athletes.some((athlete) => athlete.id === effectiveUserId)
        ? [effectiveUserId]
        : [],
    );
    setCalModal(exercise);
  };

  const handleAddToCalendar = async () => {
    if (!calModal) return;
    const targetIds = isCoach ? calAthleteIds : effectiveUserId != null ? [effectiveUserId] : [];
    if (!targetIds.length) return;
    setBusy(true);
    const ex = calModal;
    try {
      const results = await Promise.allSettled(
        targetIds.map((athleteId) => libraryApi.addExerciseToCalendar(ex.id, athleteId, calDate)),
      );
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) {
        setError(t('library.addToCalendarModal.partialFail', { failed, total: targetIds.length }));
        return;
      }
      setCalModal(null);
      navigate('/calendar');
    } catch (e) {
      setError((e as Error)?.message ?? t('library.addToCalendarFailed', 'Could not add to calendar'));
    } finally {
      setBusy(false);
    }
  };

  const openQuickEdit = (ex: LibraryExercise) => {
    const hasStructure = (ex.structure?.structure?.length ?? 0) > 0;
    setQaTitle(ex.title ?? '');
    setQaType(ex.workout_type?.id ?? '');
    setQaTss(ex.tss_planned != null ? String(ex.tss_planned) : '');
    setQaDuration(
      ex.total_time_planned != null
        ? String(Math.round(Number(ex.total_time_planned) / (hasStructure ? 1 : 60)))
        : '',
    );
    setEditExercise(ex);
  };

  const builderEditConfig: WorkoutBuilderEditConfig | undefined = useMemo(() => {
    if (!builderEdit) return undefined;
    return {
      libraryExerciseId: builderEdit.id,
      workoutTypeId: builderEdit.workout_type?.id,
      initialTitle: builderEdit.title ?? '',
      initialDragData: builderEdit.structure?.structure,
      initialSteps: apiStructureToBuilderSteps(builderEdit.structure, {
        ...structureParseOptions,
        primaryIntensityMetric: builderEdit.structure?.primaryIntensityMetric,
      }),
      structureMeta: {
        primaryLengthMetric: builderEdit.structure?.primaryLengthMetric,
        primaryIntensityMetric: builderEdit.structure?.primaryIntensityMetric,
        primaryIntensityTargetOrRange: builderEdit.structure?.primaryIntensityTargetOrRange,
      },
      onSaved: () => {
        setBuilderEdit(null);
        void fetchAll();
      },
      onCancel: () => setBuilderEdit(null),
    };
  }, [builderEdit, fetchAll, structureParseOptions]);

  const filteredLibraries = useMemo(() => {
    const normalize = (value: string) => value.normalize('NFKC').replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/[\u200c\s]+/g, ' ').trim().toLocaleLowerCase();
    const query = normalize(searchQuery);

    return data
      .map((lib) => {
        const libraryMatches = normalize(lib.lib_name).includes(query);
        const exercises = (lib.exercises ?? []).filter((exercise) => {
          const sport = [exercise.workout_type?.english_name, exercise.workout_type?.name, exercise.workout_type ? getWorkoutTypeDisplayName(exercise.workout_type, t) : ''].filter(Boolean).join(' ');
          const sportMatches = sportFilter === 'all'
            || String(exercise.workout_type?.id ?? '') === sportFilter;
          const textMatches =
            !query ||
            libraryMatches ||
            normalize(exercise.title ?? '').includes(query) ||
            normalize(sport).includes(query);
          return sportMatches && textMatches;
        });

        return { ...lib, exercises, collapsed: query || sportFilter !== 'all' ? false : lib.collapsed };
      })
      .filter((lib) => (lib.exercises ?? []).length > 0 || (!query && sportFilter === 'all'));
  }, [data, searchQuery, sportFilter, t]);

  const clearFilters = () => {
    setSearchQuery('');
    setSportFilter('all');
  };

  if (coachMenuLocked) {
    return <NmEmptyState>{t('coach.addAthleteFirst')}</NmEmptyState>;
  }

  return (
    <>
      <div className="mobile-screen mobile-workout-library">
        <div className="lib-page-heading">
          <h2 className="nm-section-title">{t('library.title')}</h2>
          <NmButton size="sm" variant="primary" onClick={() => { setLibName(''); setLibModal({ mode: 'add' }); }}>
            <IoAdd aria-hidden />{t('library.newLibrary')}
          </NmButton>
        </div>
        <p className="mobile-screen__subtitle">{t('library.subtitle')}</p>

        {!loading && data.length > 0 && (
          <div className="lib-filter-bar">
            <NmInput
              label={t('library.filter.name')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('library.filter.namePlaceholder')}
              icon={<IoSearchOutline aria-hidden />}
            />
            <NmSelect
              label={t('library.filter.sport')}
              value={sportFilter}
              onChange={(event) => setSportFilter(event.target.value)}
            >
              <option value="all">{t('library.filter.any')}</option>
              {sportTypes.map(type => (
                <option key={type.id} value={String(type.id)}>
                  {type.label}
                </option>
              ))}
            </NmSelect>
          </div>
        )}

        {(searchQuery.trim() || sportFilter !== 'all') && (
          <div className="lib-search-summary" role="status">
            <span>{displayDigits(filteredLibraries.reduce((sum, lib) => sum + (lib.exercises?.length ?? 0), 0))} {t('fab.workout')}</span>
            <button type="button" onClick={clearFilters}>{t('library.filter.clear')}</button>
          </div>
        )}

        {error && (
          <NmCard variant="inset" style={{ marginBottom: 12, color: 'var(--color-danger)' }}>
            {error}
          </NmCard>
        )}

        {loading ? (
          <NmLoadingOverlay variant="inline" />
        ) : data.length === 0 ? (
          <NmEmptyState
            actionLabel={t('library.newLibrary')}
            onAction={() => {
              setLibName('');
              setLibModal({ mode: 'add' });
            }}
          >
            {t('library.emptyLibraries')}
          </NmEmptyState>
        ) : filteredLibraries.length === 0 ? (
          <NmEmptyState actionLabel={t('library.filter.clear')} onAction={clearFilters}>
            <strong>{t('library.filter.noMatches')}</strong>
            <span>{t('library.filter.noMatchesHint')}</span>
          </NmEmptyState>
        ) : (
          filteredLibraries.map((lib) => (
            <NmCard key={lib.id} variant="raised-sm" className="lib-card">
              <div className="lib-card__header">
                <button
                  type="button"
                  className="lib-card__toggle"
                  aria-controls={`library-folder-${lib.id}`}
                  aria-expanded={!lib.collapsed}
                  onClick={() => toggleCollapse(lib.id)}
                >
                  <IoFolderOutline className="lib-card__folder-icon" aria-hidden />
                  <span className="lib-card__name">{lib.lib_name}</span>
                  <span className="lib-card__badge">{displayDigits(lib.exercises?.length ?? 0)} <span>{t('fab.workout')}</span></span>
                  <span className={`lib-card__caret${lib.collapsed ? ' is-collapsed' : ''}`}>
                    {lib.collapsed
                      ? <IoChevronBack aria-hidden />
                      : <IoChevronDown aria-hidden />}
                  </span>
                </button>
                <span className="lib-card__actions">
                  <LibIconButton
                    label={t('library.addWorkout', 'Add workout')}
                    onClick={() => setAddModal({ libraryId: lib.id, step: 'choose' })}
                  >
                    <IoAdd aria-hidden />
                  </LibIconButton>
                  <details className="lib-folder-menu">
                    <summary aria-label={t('library.editLibrary', 'Edit library')}><IoEllipsisHorizontal aria-hidden /></summary>
                    <div className="lib-folder-menu__items">
                  <LibIconButton
                    label={t('library.editLibrary', 'Edit')}
                    onClick={() => {
                      setLibName(lib.lib_name);
                      setLibModal({ mode: 'edit', id: lib.id });
                    }}
                  >
                    <IoPencilOutline aria-hidden />
                  </LibIconButton>
                  <LibIconButton
                    label={t('common.delete', 'Delete')}
                    variant="danger"
                    onClick={() =>
                      setDeleteTarget({ type: 'library', id: lib.id, name: lib.lib_name })
                    }
                  >
                    <IoTrashOutline aria-hidden />
                  </LibIconButton>
                    </div>
                  </details>
                </span>
              </div>

              {!lib.collapsed && (
                <div className="lib-card__body" id={`library-folder-${lib.id}`}>
                  {(lib.exercises ?? []).length === 0 ? (
                    <p className="lib-card__empty">{t('library.noExercises', 'No workouts in this library')}</p>
                  ) : (
                    <div className="lib-exercise-strip">
                      {(lib.exercises ?? []).map((ex) => (
                        <LibraryExerciseCard
                          key={ex.id}
                          exercise={ex}
                          onAddToCalendar={() => openCalendarAssignment(ex)}
                          onQuickEdit={() => openQuickEdit(ex)}
                          onBuilderEdit={() => setBuilderEdit(ex)}
                          onDelete={() =>
                            setDeleteTarget({
                              type: 'exercise',
                              id: ex.id,
                              name: ex.title ?? t('library.untitled', 'Untitled'),
                            })
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </NmCard>
          ))
        )}
      </div>

      {(busy || loading) && !libModal && !addModal && !calModal && !deleteTarget && (
        <NmLoadingOverlay />
      )}

      <MobileSheet
        open={!!libModal}
        onClose={() => setLibModal(null)}
        sheetClassName="library-editor-sheet"
        title={libModal?.mode === 'add' ? t('library.newLibrary') : t('library.editLibrary', 'Edit library')}
        footer={
          <>
            <NmButton variant="ghost" onClick={() => setLibModal(null)}>
              {t('common.cancel', 'Cancel')}
            </NmButton>
            <NmButton variant="primary" onClick={() => void handleSaveLib()} disabled={!libName.trim()}>
              {t('common.save', 'Save')}
            </NmButton>
          </>
        }
      >
        <NmInput
          value={libName}
          onChange={(e) => setLibName(e.target.value)}
          placeholder={t('library.libraryNamePlaceholder', 'Library name')}
        />
      </MobileSheet>

      <MobileSheet
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('library.deleteConfirmTitle', 'Delete?')}
        subtitle={deleteTarget?.name}
        footer={
          <>
            <NmButton variant="ghost" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel', 'Cancel')}
            </NmButton>
            <NmButton variant="danger" onClick={() => void handleDelete()}>
              {t('common.delete', 'Delete')}
            </NmButton>
          </>
        }
      >
        <p>{t('library.deleteConfirmBody', 'This action cannot be undone.')}</p>
      </MobileSheet>

      <MobileSheet
        open={!!addModal && addModal.step === 'choose'}
        onClose={() => setAddModal(null)}
        title={t('library.addWorkout', 'Add workout')}
        subtitle={t('library.addWorkoutHint', 'Choose how to create this workout')}
      >
        <div className="mobile-choice-list">
          <button
            type="button"
            className="mobile-choice-card"
            onClick={() => addModal && setAddModal({ ...addModal, step: 'quick' })}
          >
            <span className="mobile-choice-card__title">{t('library.quickAdd', 'Quick add')}</span>
            <span className="mobile-choice-card__desc">{t('library.quickAddDesc', 'Name, sport & duration')}</span>
          </button>
        </div>
      </MobileSheet>

      <MobileSheet
        open={!!addModal && addModal.step === 'quick'}
        onClose={() => setAddModal(null)}
        title={t('library.quickAdd', 'Quick add')}
        footer={
          <>
            <NmButton variant="ghost" onClick={() => setAddModal(null)}>
              {t('common.cancel', 'Cancel')}
            </NmButton>
            <NmButton variant="primary" onClick={() => void handleQuickAdd()} disabled={!qaTitle.trim()}>
              {t('common.save', 'Save')}
            </NmButton>
          </>
        }
      >
        <QuickAddFields
          qaTitle={qaTitle}
          setQaTitle={setQaTitle}
          qaType={qaType}
          setQaType={setQaType}
          qaDuration={qaDuration}
          setQaDuration={setQaDuration}
          qaTss={qaTss}
          setQaTss={setQaTss}
          sportTypes={sportTypes}
        />
      </MobileSheet>

      <MobileSheet
        open={!!editExercise}
        onClose={() => setEditExercise(null)}
        title={t('library.editWorkout', 'Edit workout')}
        footer={
          <>
            <NmButton variant="ghost" onClick={() => setEditExercise(null)}>
              {t('common.cancel', 'Cancel')}
            </NmButton>
            <NmButton variant="secondary" onClick={() => editExercise && setBuilderEdit(editExercise)}>
              {t('library.openBuilder', 'Builder')}
            </NmButton>
            <NmButton variant="primary" onClick={() => void handleQuickEdit()} disabled={!qaTitle.trim()}>
              {t('common.save', 'Save')}
            </NmButton>
          </>
        }
      >
        <QuickAddFields
          qaTitle={qaTitle}
          setQaTitle={setQaTitle}
          qaType={qaType}
          setQaType={setQaType}
          qaDuration={qaDuration}
          setQaDuration={setQaDuration}
          qaTss={qaTss}
          setQaTss={setQaTss}
          sportTypes={sportTypes}
        />
      </MobileSheet>

      <MobileSheet
        open={!!calModal}
        onClose={() => setCalModal(null)}
        title={t('library.addToCalendarTitle', 'Add to calendar')}
        subtitle={calModal?.title ?? undefined}
        sheetClassName="library-assignment-sheet"
        footer={
          <>
            <NmButton variant="ghost" onClick={() => setCalModal(null)} disabled={busy}>
              {t('common.cancel', 'Cancel')}
            </NmButton>
            <NmButton
              variant="primary"
              onClick={() => void handleAddToCalendar()}
              disabled={busy || (isCoach && calAthleteIds.length === 0)}
            >
              <IoCalendarOutline aria-hidden />
              {busy
                ? t('library.addToCalendarModal.assigning', 'Assigning…')
                : isCoach
                  ? t('library.addToCalendarModal.assign', 'Assign to selected')
                  : t('library.addToCalendarTitle', 'Add to calendar')}
            </NmButton>
          </>
        }
      >
        <label className="nm-input-label">{t('library.addToCalendarModal.date', 'Date')}</label>
        <NmIsoDateField
          value={calDate}
          onChange={setCalDate}
          ariaLabel={t('library.addToCalendarModal.date', 'Date')}
          sheetZIndex={14600}
        />
        <div className="lib-date-chips">
          {[0, 1, 3, 7].map((d) => {
            const dt = new Date();
            dt.setDate(dt.getDate() + d);
            const iso = dt.toISOString().slice(0, 10);
            const lbl =
              d === 0
                ? t('library.today', 'Today')
                : d === 1
                  ? t('library.tomorrow', 'Tomorrow')
                  : formatWeekdayDate(iso, i18n.language);
            return (
              <button
                key={d}
                type="button"
                className={`lib-date-chip${calDate === iso ? ' lib-date-chip--active' : ''}`}
                onClick={() => setCalDate(iso)}
              >
                {lbl}
              </button>
            );
          })}
        </div>

        {isCoach && (
          <section className="library-assignment-sheet__athlete-section">
            <div className="library-assignment-sheet__athlete-heading">
              <span>{t('library.addToCalendarModal.athletes', 'Athletes')}</span>
              {athletes.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setCalAthleteIds(
                      calAthleteIds.length === athletes.length
                        ? []
                        : athletes.map((athlete) => athlete.id),
                    )
                  }
                >
                  {calAthleteIds.length === athletes.length
                    ? t('library.addToCalendarModal.clearAll', 'Clear all')
                    : t('library.addToCalendarModal.selectAll', 'Select all')}
                </button>
              )}
            </div>

            {athletes.length > 0 ? (
              <div className="library-assignment-sheet__athletes">
                {athletes.map((athlete) => {
                  const checked = calAthleteIds.includes(athlete.id);
                  return (
                    <label
                      key={athlete.id}
                      className={checked ? 'is-selected' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setCalAthleteIds((current) =>
                            checked
                              ? current.filter((id) => id !== athlete.id)
                              : [...current, athlete.id],
                          )
                        }
                      />
                      <span>{athlete.full_name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="library-assignment-sheet__empty">
                {t('library.addToCalendarModal.noAthletes', 'No athletes on your roster yet.')}
              </p>
            )}

            <p className="library-assignment-sheet__selected-count">
              {t('library.addToCalendarModal.selectedCount', { count: calAthleteIds.length })}
            </p>
          </section>
        )}
      </MobileSheet>

      {builderEdit && builderEditConfig && (
        <div className="mobile-fullscreen-panel vo2-slide-up-fullscreen">
          <WorkoutBuilderPage edit={builderEditConfig} userId={effectiveUserId ?? undefined} />
        </div>
      )}
    </>
  );
}

function LibraryExerciseCard({
  exercise: ex,
  onAddToCalendar,
  onQuickEdit,
  onBuilderEdit,
  onDelete,
}: {
  exercise: LibraryExercise;
  onAddToCalendar: () => void;
  onQuickEdit: () => void;
  onBuilderEdit: () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const hasStructure = (ex.structure?.structure?.length ?? 0) > 0;
  const sport = ex.workout_type ? getWorkoutTypeDisplayName(ex.workout_type, t) : t('fab.workout');
  const timeLabel = ex.total_time_planned
    ? formatPlannedDuration(ex.total_time_planned, hasStructure)
    : null;
  const duration = timeLabel?.replace(/(\d)h\b/g, i18n.language.startsWith('fa') ? '$1 ساعت' : '$1h').replace(/(\d)m\b/g, i18n.language.startsWith('fa') ? '$1 دقیقه' : '$1m');

  return (
    <article className="lib-exercise-card">
      <div className="lib-exercise-card__heading">
        <div className="lib-exercise-card__identity">
          <span className="lib-exercise-card__sport"><WorkoutTypeIcon typeId={ex.workout_type?.id} name={sport} size={20} />{sport}</span>
          <h4 className="lib-exercise-card__title">{ex.title || '—'}</h4>
        </div>
        <details className="lib-folder-menu lib-exercise-menu">
          <summary aria-label={`${t('library.editWorkout', 'Edit workout')} — ${ex.title || sport}`}><IoEllipsisHorizontal aria-hidden /></summary>
          <div className="lib-folder-menu__items">
            <button type="button" onClick={hasStructure ? onBuilderEdit : onQuickEdit}><IoPencilOutline aria-hidden />{t('library.editWorkout', 'Edit workout')}</button>
            <button type="button" className="lib-menu-delete" onClick={onDelete}><IoTrashOutline aria-hidden />{t('common.delete', 'Delete')}</button>
          </div>
        </details>
      </div>
      <div className="lib-exercise-card__metrics">
        <div><span>{t('fab.metricTime', i18n.language.startsWith('fa') ? 'زمان' : 'Duration')}</span><strong>{duration ? displayDigits(duration) : '—'}</strong></div>
        <div><span>{t('workoutForm.summary.tss', i18n.language.startsWith('fa') ? 'بار تمرین' : 'Training load')}</span><strong><bdi>{ex.tss_planned != null ? displayDigits(Math.round(Number(ex.tss_planned))) : '—'}</bdi> <small>TSS</small></strong></div>
        {ex.distance_planned != null && <div><span>{i18n.language.startsWith('fa') ? 'مسافت' : 'Distance'}</span><strong><bdi>{displayDigits(Number(ex.distance_planned))}</bdi> <small>km</small></strong></div>}
      </div>
      {hasStructure && (
        <div className="lib-exercise-card__chart ltr-island">
          <WorkoutStructureBarMini structure={ex.structure} width={400} height={44} />
        </div>
      )}
      <div className="lib-exercise-card__actions">
        <button type="button" className="lib-calendar-action" onClick={onAddToCalendar}>
          <IoCalendarOutline aria-hidden />{t('library.addToCalendarTitle', 'Add to calendar')}
        </button>
      </div>
    </article>
  );
}

function LibIconButton({
  children,
  label,
  variant,
  size = 'md',
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  variant?: 'danger' | 'primary';
  size?: 'md' | 'xs';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[
        'lib-icon-btn',
        size === 'xs' ? 'lib-icon-btn--xs' : '',
        variant === 'danger' ? 'lib-icon-btn--danger' : '',
        variant === 'primary' ? 'lib-icon-btn--primary' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function QuickAddFields({
  qaTitle,
  setQaTitle,
  qaType,
  setQaType,
  qaDuration,
  setQaDuration,
  qaTss,
  setQaTss,
  sportTypes,
}: {
  qaTitle: string;
  setQaTitle: (v: string) => void;
  qaType: number | '';
  setQaType: (v: number | '') => void;
  qaDuration: string;
  setQaDuration: (v: string) => void;
  qaTss: string;
  setQaTss: (v: string) => void;
  sportTypes: { id: number; label: string }[];
}) {
  const { t } = useTranslation();
  return (
    <div className="mobile-form-stack">
      <label className="nm-input-label">{t('library.workoutTitle', 'Title')}</label>
      <NmInput value={qaTitle} onChange={(e) => setQaTitle(e.target.value)} />
      <label className="nm-input-label">{t('library.sportType', 'Sport')}</label>
      <NmSelect
        value={qaType === '' ? '' : String(qaType)}
        onChange={(e) => setQaType(e.target.value === '' ? '' : Number(e.target.value))}
      >
        <option value="">{t('library.selectSport', '— Select —')}</option>
        {sportTypes.map((st) => (
          <option key={st.id} value={st.id}>
            {st.label}
          </option>
        ))}
      </NmSelect>
      <div className="mobile-form-row">
        <div>
          <label className="nm-input-label">{t('library.durationMin', 'Duration (min)')}</label>
          <NmInput type="number" value={qaDuration} onChange={(e) => setQaDuration(e.target.value)} dir="ltr" />
        </div>
        <div>
          <label className="nm-input-label">TSS</label>
          <NmInput type="number" value={qaTss} onChange={(e) => setQaTss(e.target.value)} dir="ltr" />
        </div>
      </div>
    </div>
  );
}
